import { motion } from "framer-motion";
import { Headphones, Wallet, CheckCircle2, Sparkles } from "lucide-react";

export default function Features() {
  return (
    <section className="relative py-24 md:py-32 bg-black" data-testid="features-section">
      <div className="max-w-7xl mx-auto px-6 md:px-10">
        <div className="mb-14 max-w-2xl">
          <span className="text-xs font-display uppercase tracking-[0.4em] text-[#BEDD7D]">
            / Ecosistema
          </span>
          <h2 className="mt-6 font-display font-bold text-4xl md:text-5xl uppercase tracking-tight leading-none">
            Todo lo que necesitas para operar con{" "}
            <span className="italic text-[#BEDD7D]">ventaja</span>.
          </h2>
        </div>

        <div className="grid lg:grid-cols-2 gap-5">
          {/* Cuenta Fondeada */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7 }}
            className="relative group rounded-3xl border border-[#3E4D36]/60 bg-gradient-to-br from-[#1A2E1A]/60 to-black p-8 md:p-10 overflow-hidden"
            data-testid="feature-fondeo"
          >
            <div className="flex items-center gap-3 mb-6">
              <Wallet className="w-5 h-5 text-[#BEDD7D]" />
              <span className="text-xs font-display uppercase tracking-[0.3em] text-white/60">
                Cuenta Fondeada
              </span>
            </div>
            <h3 className="font-display font-bold text-3xl md:text-4xl uppercase leading-none">
              Operamos tu evaluación <span className="text-[#BEDD7D]">contigo</span>.
            </h3>
            <p className="mt-4 text-white/70 font-body">
              Diseñada para avanzar en tu carrera como trader. Método 3M + Lanza de Neptuno + acompañamiento
              Falcons para pasar tu prueba y empezar a retirar.
            </p>

            <ul className="mt-6 space-y-2.5">
              {[
                "Prueba de fondeo $100.000",
                "Bitácora de misiones y métricas",
                "Revisión de setups con mentores y con agentes de IA entrenados por Honor Trading",
              ].map((i) => (
                <li key={i} className="flex items-center gap-2 text-sm text-white/75 font-body">
                  <CheckCircle2 className="w-4 h-4 text-[#BEDD7D] shrink-0" />
                  {i}
                </li>
              ))}
            </ul>

            {/* Chart mock */}
            <div className="mt-10 relative h-40 rounded-xl bg-black/60 border border-[#3E4D36]/40 p-4 overflow-hidden">
              <svg viewBox="0 0 400 120" className="w-full h-full" preserveAspectRatio="none">
                <path
                  d="M0 90 L40 80 L80 92 L120 70 L160 75 L200 60 L240 55 L280 40 L320 35 L360 25 L400 18"
                  stroke="#BEDD7D"
                  strokeWidth="2"
                  fill="none"
                />
                <path
                  d="M0 90 L40 80 L80 92 L120 70 L160 75 L200 60 L240 55 L280 40 L320 35 L360 25 L400 18 L400 120 L0 120 Z"
                  fill="url(#grad)"
                  opacity="0.3"
                />
                <defs>
                  <linearGradient id="grad" x1="0" x2="0" y1="0" y2="1">
                    <stop offset="0%" stopColor="#BEDD7D" />
                    <stop offset="100%" stopColor="#BEDD7D" stopOpacity="0" />
                  </linearGradient>
                </defs>
              </svg>
              <div className="absolute top-3 left-4 text-[10px] font-display uppercase tracking-widest text-white/40">
                Equity curve
              </div>
              <div className="absolute top-3 right-4 text-[10px] font-display uppercase tracking-widest text-[#BEDD7D]">
                +42.8%
              </div>
            </div>
          </motion.div>

          {/* Soporte 24/7 */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="relative group rounded-3xl border border-[#3E4D36]/60 bg-gradient-to-br from-[#1A2E1A]/60 to-black p-8 md:p-10 overflow-hidden"
            data-testid="feature-support"
          >
            <div className="flex items-center gap-3 mb-6">
              <Headphones className="w-5 h-5 text-[#BEDD7D]" />
              <span className="text-xs font-display uppercase tracking-[0.3em] text-white/60">
                Soporte 24/7
              </span>
            </div>
            <h3 className="font-display font-bold text-3xl md:text-4xl uppercase leading-none">
              Escuadrón Falcons,{" "}
              <span className="text-[#BEDD7D]">en tu idioma</span>.
            </h3>
            <p className="mt-4 text-white/70 font-body">
              Consultas de lunes a viernes con Falcons senior, trading en vivo cada mañana y
              comunidad abierta en Discord y Telegram.
            </p>

            {/* Chat mock */}
            <div className="mt-8 space-y-3">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 rounded-full bg-[#BEDD7D] text-black flex items-center justify-center font-display font-bold text-sm">
                  F
                </div>
                <div className="bg-[#1A2E1A]/80 rounded-2xl rounded-tl-sm px-4 py-2.5 max-w-[70%] border border-[#3E4D36]/40">
                  <div className="text-[10px] font-display uppercase tracking-widest text-[#BEDD7D] mb-1">Falcon Jose</div>
                  <div className="text-sm text-white/90">Setup confirmado en EUR/USD, revisa el plan</div>
                </div>
              </div>
              <div className="flex items-start gap-3 justify-end">
                <div className="bg-[#BEDD7D] rounded-2xl rounded-tr-sm px-4 py-2.5 max-w-[70%]">
                  <div className="text-sm text-black">Gracias Falcon! <Sparkles className="inline w-3.5 h-3.5" /></div>
                </div>
              </div>
              <div className="flex items-center gap-2 pl-11 pt-1">
                <div className="flex gap-1">
                  <span className="w-1.5 h-1.5 rounded-full bg-white/30 animate-pulse" />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/30 animate-pulse" style={{animationDelay:'150ms'}} />
                  <span className="w-1.5 h-1.5 rounded-full bg-white/30 animate-pulse" style={{animationDelay:'300ms'}} />
                </div>
                <span className="text-xs text-white/40 font-body">Falcon Ana está escribiendo...</span>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
